/* eslint-disable jsdoc/check-param-names */
import { Dispatch, SetStateAction, useRef } from "react";
import { SxProps, TextField } from "@mui/material";
import { INT_MAX } from "../constants";

type Props = {
  id: string;
  type?: string;
  label: string;
  text: string;
  setText: Dispatch<SetStateAction<any>>;
  maxLength?: number;
  intMax?: number;
  variant?: "outlined" | "filled" | "standard";
  onChange?: Function;
  size?: "small" | "medium";
  fullWidth?: boolean;
  maxWidth?: number | string;
  width?: number | string;
  sx?: SxProps;
  required?: boolean;
  disabled?: boolean;
  paleDisabled?: boolean;
  isNumericText?: boolean;
  isPostalCd?: boolean;
  allowHalfKanaOnly?: boolean;
  error?: string;
};

/**
 * カスタムテキストフィールド
 * @param id フィールドID
 * @param type タイプ
 * @param label ラベル
 * @param text テキスト
 * @param setText テキストをセット
 * @param maxLength 最大文字数
 * @param intMax 数字上の最大値
 * @param variant テキストフィールドの種類
 * @param size inputの大きさ
 * @param fullWidth 幅いっぱいにするかどうか
 * @param maxWidth 要素の最大幅(fullWidthで幅いっぱいでも制限されるので、長くしたい場合に使用)
 * @param width 幅
 * @param sx SxProps
 * @param required 必須かどうか
 * @param disabled 非活性
 * @param paleDisabled 薄い色の非活性
 * @param isNumericText 半角数字のみのテキスト
 * @param postalCd 郵便番号
 * @param allowHalfKanaOnly 半角ｶﾅのみ
 * @param error エラーメッセージだが、表示はしない。
 * @returns JSX.Element
 */
export default function CustomTextField({
  id,
  type = "text",
  label,
  text,
  setText,
  maxLength = 100,
  intMax,
  variant = "outlined",
  onChange = () => {},
  size = "small",
  fullWidth,
  maxWidth = 223,
  width = 'inherit',
  sx,
  required,
  disabled,
  paleDisabled,
  isNumericText,
  isPostalCd,
  allowHalfKanaOnly,
  error,
}: Props) {
  // IMF入力中か
  const composing = useRef(false);

  // ↓↓↓ 半角ｶﾅのみ許可コード
  const sanitizeHalfKana = (s: string) => (
    // 半角ASCII + 半角カナのみ残す
    s.replace(/[^\x20-\x7E\uFF61-\uFF9F]/g, "")
  );

  const handleChangeHalf = (value: string) => {
    if (composing.current) {
      // 変換中は触らない（IME対策）
      setText(value);
    } else {
      setText(sanitizeHalfKana(value));
    }
    onChange();
  };
  // ↑↑↑ 半角ｶﾅのみ許可コード

  // ↓↓↓ 郵便番号のため
  const format = (raw: string) => {
    // 全角→半角の正規化 → 数字だけ抽出 → 7桁に制限
    const digits = raw.normalize("NFKC").replace(/\D/g, "").slice(0, 7);
    console.log(raw.normalize("NFKC"), raw.normalize("NFKC").replace(/\D/g, ""), digits);
    
    return digits.length > 3
      ? `${digits.slice(0, 3)}-${digits.slice(3)}`
      : digits;
  };

  const handleChangePostalCd = (value: string) => {
    console.log("change");
      const next = composing.current ? value : format(value);
      setText(next);
      onChange();
  };

  const handleCompositionStart = (e: React.CompositionEvent<HTMLInputElement>) => {
    if (allowHalfKanaOnly) composing.current = true;
    if (isPostalCd) composing.current = true;
  }
  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    if (allowHalfKanaOnly) {
      composing.current = false;
      const val = (e.target as HTMLInputElement).value
      const sanitizedVal = sanitizeHalfKana(val);
      if (sanitizedVal !== val) setText(sanitizedVal);
    }
    if (isPostalCd) {
      // const val = e.currentTarget.value
      // const val = (e.target as HTMLInputElement).value
      // const next = format(val);
      // if (next !== val) setText(next);
      composing.current = false;
    }
  };
  const checkPostalCd = (value: string) => /^[0-9]{3}-[0-9]{4}$/.test(value);
  // ↑↑↑ 郵便番号のため

  const onChangeText = (value: string) => {
    if (allowHalfKanaOnly) {
      handleChangeHalf(value);
      return;
    }
    if (isPostalCd) {
      // 郵便番号なら
      handleChangePostalCd(value);
      return;
    }
    if (isNumericText && !/^\d*$/.test(value)) {
      // 数値テキストで数値以外の文字が入力されたら無視
      return;
    }
    setText(value);
    onChange();
  };
  return (
    <TextField
      id={id}
      type={type}
      value={text}
      label={label}
      inputProps={
        {...(isNumericText && type === "Number"
          ? {
              sx: { fontSize: 12, textAlign: "right" },
              min: 0,
              max: intMax ?? INT_MAX,
            }
          : isPostalCd
          ? {
            // maxLength: 8,
            pattern: "^[0-9]{3}-[0-9]{4}$",
            inputMode: "numeric",
          }
          : {
              maxLength,
            }),
          pattern: allowHalfKanaOnly ? "[\\x20-\\x7E\\uFF61-\\uFF9F]*" : {},
        }
      }
      variant={variant}
      onChange={(e) => {
        if (isNumericText && type === "Number" && Number(e.target.value) > INT_MAX) {
          return;
        }
        onChangeText(e.target.value)}
      }
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onBlur={() => isPostalCd ? handleChangePostalCd(text) : null}
      size={size}
      fullWidth={fullWidth}
      required={required}
      disabled={disabled || paleDisabled}
      sx={{ ...{ maxWidth, width }, ...({
        "&& .Mui-disabled": paleDisabled ? {
          color: "#eaeaea",
        } : {},
        "&& .MuiOutlinedInput-notchedOutline": paleDisabled ? {
          borderColor: "#eaeaea", // 囲み
        } : {},
      }), ...sx }}
      onKeyDown={(e) => {
        if (isNumericText && (e.key === "-" || e.key === '.')) e.preventDefault();
      }}
      error={isPostalCd
        ? (text !== "" && !checkPostalCd(text) )
        : !!error
      }
    />
  );
}
